// ─────────────────────────────────────────────────────────────────────────────
// LandingPriorityResolver.js
// Pure-logic module: takes a DashboardSnapshot and returns the highest-priority
// live risk to surface in the Startup Readiness Sweep landing card.
//
// Priority order (from spec UI-LAUNCH-001 §4.3):
//   qcHold > unlinkedPieces (preTrace only) > missingMtr > overweightLoad
//   > materialShortage > ready
//
// Returns: { reason, label, description, severity, count? }
//   severity: 'error' | 'warning' | 'success'
// ─────────────────────────────────────────────────────────────────────────────

/**
 * @param {object|null} snapshot  DashboardSnapshot from /api/reports/snapshot
 * @returns {{ reason: string, label: string, description: string, severity: string, count?: number }}
 */
export function fromSnapshot(snapshot) {
  if (!snapshot) {
    return {
      reason:      'loading',
      label:       'Reading shop floor…',
      description: 'Checking live status',
      severity:    'info',
    };
  }

  // 1. QC Holds — NCRs with pending disposition (highest risk, blocks shipment)
  if (snapshot.qcHolds > 0) {
    return {
      reason:      'qcHold',
      label:       `${snapshot.qcHolds} QC Hold${snapshot.qcHolds > 1 ? 's' : ''}`,
      description: 'Non-conformances pending disposition',
      severity:    'error',
      count:       snapshot.qcHolds,
    };
  }

  // 2. Unlinked pieces — only when preTrace is active (heat traceability on)
  if (snapshot.preTraceEnabled && snapshot.unlinkedPieceCount > 0) {
    return {
      reason:      'unlinkedPieces',
      label:       `${snapshot.unlinkedPieceCount} Unlinked Piece${snapshot.unlinkedPieceCount > 1 ? 's' : ''}`,
      description: 'Fabricated items missing heat assignment',
      severity:    'warning',
      count:       snapshot.unlinkedPieceCount,
    };
  }

  // 3. Missing MTRs — heats with no CMTR on file
  if (snapshot.missingMtrCount > 0) {
    return {
      reason:      'missingMtr',
      label:       `${snapshot.missingMtrCount} Missing MTR${snapshot.missingMtrCount > 1 ? 's' : ''}`,
      description: 'Heat records without CMTR documentation',
      severity:    'warning',
      count:       snapshot.missingMtrCount,
    };
  }

  // 4. Overweight load blocked
  if (snapshot.overweightLoadBlocked) {
    return {
      reason:      'overweightLoad',
      label:       'Overweight Load Blocked',
      description: 'A shipping load exceeds the permitted weight limit',
      severity:    'error',
    };
  }

  // 5. Material shortages
  if (snapshot.materialShortages > 0) {
    return {
      reason:      'materialShortage',
      label:       `${snapshot.materialShortages} Material Shortage${snapshot.materialShortages > 1 ? 's' : ''}`,
      description: 'Jobs with insufficient material on hand',
      severity:    'warning',
      count:       snapshot.materialShortages,
    };
  }

  // 6. All clear
  return {
    reason:      'ready',
    label:       'All Clear',
    description: 'No active risks detected on the floor',
    severity:    'success',
  };
}
